import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import isEqual from "lodash/isEqual";
import { withStyles } from "@material-ui/core/styles";
import withWidth from "@material-ui/core/withWidth/withWidth";
import Typography from "@material-ui/core/Typography/Typography";
import Dialog from "@material-ui/core/Dialog/Dialog";
import DialogContent from "@material-ui/core/DialogContent/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions/DialogActions";
import Icon from "@material-ui/core/Icon/Icon";
import RootRef from "@material-ui/core/RootRef/RootRef";
import { InView } from "react-intersection-observer";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import Notification from "@configuratorware/configurator-frontendgui/App/Shared/Components/Notification";
import ReceiveOfferForm from "@configuratorware/configurator-frontendgui/App/Shared/Components/ReceiveOfferForm";
import _ from "lodash";
import AddToCart from "../AddToBasket";
import AcceptDesign from "../AcceptDesign";
import PriceOverview from "../PriceOverview";
import PriceList from "../PriceList";
import AmountPriceWrapper from "./AmountPriceWrapper";
import ProductVariant from "../../Containers/ProductVariant";

const styles = (theme) => ({
  contentPositioning: {
    paddingLeft: 32,
    paddingRight: 20,
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 0,
      paddingRight: 8,
      marginRight: "-8px",
    },
    "& p": {
      fontSize: 15,
      letterSpacing: "normal",
    },
    marginRight: 4,
    padding: 0,
  },
  dialogWrapper: {
    borderTop: `7px solid ${theme.palette.primary.main}`,
    padding: "0 32px",
    overflow: "hidden",
    [theme.breakpoints.down("xs")]: {
      padding: "0 16px",
    },
  },

  dialogPositioning: {
    overflow: "hidden",
    "& .MuiDialog-container": {
      "& .MuiDialogTitle-root": {
        paddingTop: 25,
        paddingBottom: 15,
        "& p, h2": {
          fontSize: 24,
          fontWeight: "bold",
          letterSpacing: "normal",
        },
      },
      "& .MuiDialogActions-root": {
        paddingBottom: 24,
        [theme.breakpoints.down("xs")]: {
          marginRight: 0,
        },
        marginRight: 8,
      },
      "& .MuiDialogTitle-root, .MuiDialogActions-root": {
        paddingLeft: 32,
        paddingRight: 32,
        [theme.breakpoints.down("xs")]: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        "& p": {
          fontSize: 15,
          letterSpacing: "normal",
        },
      },
    },
    "& .MuiButton-root": {
      height: 40,
      textTransform: "none",
      minWidth: 140,
      marginTop: 8,
      color: theme.palette.primary.darkText,
      [theme.breakpoints.down("xs")]: {
        height: 56,
      },
    },
  },

  closeIcon: {
    position: "absolute",
    right: 32,
    top: 32,
    [theme.breakpoints.down("xs")]: {
      right: 16,
    },
    "&:hover": {
      cursor: "pointer",
    },
  },
  dialogTitleRoot: {
    padding: "20px 0",
  },
});

const DialogActionsWrapper = ({ onTouchMove, ...props }) => {
  const ref = useRef();
  useEffect(() => {
    const { current } = ref;
    if (current) {
      current.addEventListener("touchmove", onTouchMove, { passive: false });
    }
    return () => {
      if (current) {
        current.removeEventListener("touchmove", onTouchMove, {
          passive: false,
        });
      }
    };
  }, [onTouchMove]);
  return (
    // as of material-ui v3.x the refs are not forwarded, so the only way to get it is using RootRef
    // FIXME check after updating material-ui
    <RootRef rootRef={ref}>
      <DialogActions {...props} />
    </RootRef>
  );
};

DialogActionsWrapper.propTypes = {
  onTouchMove: PropTypes.func,
};

class AmountPrice extends React.Component {
  static propTypes = {
    boxed: PropTypes.bool,
    classes: PropTypes.object,
    amount: PropTypes.number,
    priceFormatted: PropTypes.string,
    vatNotice: PropTypes.string,
    showConfigurationModeSwitch: PropTypes.bool,
    error: PropTypes.object,
    width: PropTypes.string,
    hidePrices: PropTypes.bool,
    priceList: PropTypes.array,
    showProductVariants: PropTypes.bool,
    title: PropTypes.string,
    showReceiveOfferForm: PropTypes.bool,
    onClose: PropTypes.func,
    sendReceiveOfferRequest: PropTypes.func,
    setConfigurationComment: PropTypes.func,
    onSelectableChanged: PropTypes.func,
    onAddToBasket: PropTypes.func,
    isBulkNamesSelected: PropTypes.bool,
    clientTexts: PropTypes.object,
    bulkSavings: PropTypes.array,
    notifications: PropTypes.array,
    displayAcceptDesign: PropTypes.bool,
    bulkNameErrors: PropTypes.array,
    onShowChange: PropTypes.func,
    wrapperClass: PropTypes.string,
    validateConfiguration: PropTypes.func,
  };

  static defaultProps = {
    showConfigurationModeSwitch: false,
    notifications: [],
    clientTexts: {
      termsAndConditionsLink: "",
    },
  };

  state = {
    showPriceOverview: false,
    notice: "",
    hasErrorReceiveOfferForm: false,
    bulkNameErrors: [],
    approval: {
      value: false,
      error: null,
    },
    inView: true,
    showErrors: false,
  };

  componentDidUpdate(prevProps, prevState) {
    const { error, bulkNameErrors } = this.props;
    if (
      error &&
      !isEqual(error, prevProps.error) &&
      this.dialogContentRef.current
    ) {
      this.dialogContentRef.current.scrollTo(0, 0);
    }
    if (bulkNameErrors !== prevProps.bulkNameErrors) {
      this.setState({ bulkNameErrors });
    }
    const { showPriceOverview } = this.state;
    if (showPriceOverview !== prevState.showPriceOverview) {
      const { onShowChange } = this.props;
      onShowChange && onShowChange(showPriceOverview);
    }
  }

  receiveOfferFormComponentRef = React.createRef();

  dialogContentRef = React.createRef();

  priceListRef = React.createRef();

  handleChange = (event) => {
    this.setState({
      [event.target.name]: {
        value: event.target.checked,
        error: null,
      },
    });
  };

  verifyRequestErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = this.props;
    console.log("verifyRequestErrors", bulkNameErrors);
    const designApproved = this.state.approval.value;
    if (bulkNameErrors && bulkNameErrors.length > 0) {
      this.setState({ showErrors: true });
      this.setState({ bulkNameErrors });
      this.dialogContentRef.current.scrollTo(0, 0);
    }
    if (displayAcceptDesign && !designApproved) {
      this.setState({
        approval: {
          value: false,
          error: true,
        },
      });
    }
  };

  requestHasErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = this.props;
    const designApproved = this.state.approval.value;
    if (
      (bulkNameErrors && bulkNameErrors.length > 0) ||
      (displayAcceptDesign && !designApproved)
    ) {
      return true;
    }
    return false;
  };

  onAddToBasket = () => {
    const { onAddToBasket } = this.props;
    if (!this.requestHasErrors()) {
      onAddToBasket();
      this.handleClose();
    } else {
      this.verifyRequestErrors();
    }
  };

  handleTouchMove = (evt) => {
    const dialogContent = this.dialogContentRef.current;
    evt.preventDefault();
    dialogContent.scrollTo(0, dialogContent.scrollTop);
  };

  openPriceOverview = async () => {
    try {
      await this.props.validateConfiguration();
      this.setState({
        showPriceOverview: !this.state.showPriceOverview,
        hasErrorReceiveOfferForm: false,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Error while validating configuration: ${e}`);
    }
  };

  handleClose = () => {
    this.setState({
      showPriceOverview: false,
      bulkNameErrors: [],
      approval: {
        value: false,
        error: null,
      },
      showErrors: false,
    });
    this.props.onClose();
  };

  onReceiveOfferFormPress = async () => {
    const result = await this.props.sendReceiveOfferRequest(
      this.receiveOfferFormComponentRef.current.validate()
    );
    const designApproved = this.state.approval.value;
    if (designApproved && !this.requestHasErrors()) {
      if (!result) {
        this.setState({ hasErrorReceiveOfferForm: true });
        this.dialogContentRef.current.scrollTo(0, 0);
      }
      if (result) {
        this.setState({ hasErrorReceiveOfferForm: false });
        this.handleClose();
      }
    } else {
      this.verifyRequestErrors();
    }
  };

  handleBasketNoticeChange = (event) => {
    this.setState({ notice: event.target.value });
    this.props.setConfigurationComment(event.target.value);
  };

  onSelectableChanged = (priceData) => (key, newSelected) => {
    const nextData = priceData.map((currentElement) => {
      if (currentElement.key === key) {
        return {
          ...currentElement,
          selected: newSelected,
          price: newSelected ? "10,00 €" : "0,00 €",
        };
      }
      return currentElement;
    });
    this.setState({ priceData: nextData });
  };

  priceListClick = () => {
    this.priceListRef.current.scrollIntoView();
  };

  showPriceListButton = (inView) => {
    this.setState({ inView });
  };
  /*
    pmsAmountIsEnough = _.memoize(({
      amount, itemAttributes, pmsIsSelected,
    }) => {
      if (!pmsIsSelected) return true;
      let minAmountPMS = null;
      let noPriceAmountPMS = null;
      itemAttributes.some((attr) => {
        if (attr.identifier === 'minAmountPMS') minAmountPMS = attr.values[0].value;
        if (attr.identifier === 'noPriceAmountPMS') noPriceAmountPMS = attr.values[0].value;
        return !!minAmountPMS;
      });
      const minAmountValue = minAmountPMS || noPriceAmountPMS;
      return (amount < minAmountValue);
    }) */

  isDisabled = _.memoize(
    ({ error }) => !!error

    // return (this.pmsAmountIsEnough({ amount, itemAttributes, pmsIsSelected }) || !!error);
    // return !!error || (amount < 3000 && notice.length > 0);
  );

  renderPriceOverview() {
    const {
      notice,
      showPriceOverview,
      approval,
      inView,
      showErrors,
      bulkNameErrors,
    } = this.state;
    const {
      amount,
      maxAmount,
      priceList,
      priceFormatted,
      onSelectableChanged,
      showProductVariants,
      error,
      width,
      hidePrices,
      title,
      classes,
      showReceiveOfferForm,
      vatNotice,
      clientTexts,
      displayAcceptDesign,
      notifications,
      showCallToAction,
    } = this.props;

    const { termsAndConditionsLink } = clientTexts;
    const mergedNotifications = [...notifications] || [];

    if (showErrors && bulkNameErrors && bulkNameErrors.length > 0) {
      bulkNameErrors.map((error) => {
        mergedNotifications.push({
          message: error,
          level: "error",
          color: "error",
        });
      });
    }
    if (error && error.errors.length) {
      console.log("mergedNotifications error", error);
      for (const fieldError of error.errors) {
        mergedNotifications.push({
          message: t("amountPrice.minimumAmountError", {
            minimumAmount: fieldError.minimumAmount,
            itemTitle: fieldError.itemTitle,
            selectedAmount: fieldError.selectedAmount,
          }),
          level: "error",
          color: "error",
        });
      }
    }
    console.log({ bulkNameErrors, mergedNotifications });

    return (
      <Dialog
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        classes={{
          paper: classes.dialogWrapper,
          container:
            width === "md" || width === "xs" ? classes.dialogPositioning : "",
          root: classes.dialogPositioning,
        }}
        BackdropProps={{ className: classes.dialogPositioning }}
        onClose={this.handleClose.bind(this)}
        open={showPriceOverview}
        maxWidth="md"
        fullWidth
        fullScreen={width === "sm" || width === "xs"}
        scroll="paper"
      >
        <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
          <Typography variant="body1">
            <b>
              {t(
                showReceiveOfferForm
                  ? "receiveOfferForm.sendButton"
                  : "amountPrice.intoCart"
              )}
            </b>
          </Typography>
          <Icon
            className={classes.closeIcon}
            onClick={this.handleClose.bind(this)}
          >
            close
          </Icon>
        </DialogTitle>
        <RootRef rootRef={this.dialogContentRef}>
          <DialogContent classes={{ root: classes.contentPositioning }}>
            {this.state.hasErrorReceiveOfferForm && (
              <Typography color="error" variant="body2">
                {t("receiveOfferForm.checkInputsInForm")}
              </Typography>
            )}
            <PriceOverview
              title={t("amountPrice.productVariants")}
              contactData={t("amountPrice.contactData")}
              handleClose={this.handleClose}
              amount={amount}
              maxAmount={maxAmount}
              acceptDesignComponent={
                <AcceptDesign
                  termsAndConditionsLink={termsAndConditionsLink}
                  displayAcceptDesign={displayAcceptDesign}
                  handleChangeCheckboxApproval={this.handleChange}
                  approval={approval}
                  closeProductOverviewDialog={this.handleClose}
                  notice={notice}
                  noteLabel={t("amountPrice.notice")}
                  handleChange={this.handleBasketNoticeChange}
                />
              }
              notificationComponent={mergedNotifications.map(
                ({ message, level, color }, key) => (
                  <Notification
                    key={key}
                    icon={level}
                    color={color}
                    notification={message}
                  />
                )
              )}
              priceListComponent={
                <div ref={this.priceListRef}>
                  <InView
                    as="div"
                    onChange={(inView) => this.showPriceListButton(inView)}
                  >
                    <PriceList
                      amount={amount}
                      list={priceList}
                      totalPrice={priceFormatted}
                      taxNote={t("amountPrice.vatNotice")}
                      onSelectableChanged={onSelectableChanged}
                      hidePrices={hidePrices}
                      showReceiveOfferForm={showReceiveOfferForm}
                      productTitle={title}
                    />
                  </InView>
                </div>
              }
              productVariantComponent={
                showProductVariants && <ProductVariant />
              }
              receiveOfferFormComponent={
                <ReceiveOfferForm
                  innerRef={this.receiveOfferFormComponentRef}
                />
              }
              showReceiveOfferForm={showReceiveOfferForm}
            />
          </DialogContent>
        </RootRef>
        <DialogActionsWrapper onTouchMove={this.handleTouchMove}>
          <AddToCart
          className="clickOn_AddToCart"
            buttonLabel={t(
              showReceiveOfferForm
                ? "receiveOfferForm.sendButton"
                : "amountPrice.intoCart"
            )}
            showCallToAction={showCallToAction}
            onPress={
              showReceiveOfferForm
                ? this.onReceiveOfferFormPress
                : this.onAddToBasket
            }
            handleChange={this.handleBasketNoticeChange}
            disabled={!!error}
            totalPrice={priceFormatted}
            taxNote={vatNotice}
            showPriceList={!inView}
            priceListLabel={t("amountPrice.showPriceList")}
            priceListClick={this.priceListClick}
            hidePrices={hidePrices}
            amount={amount}
            showReceiveOfferForm={showReceiveOfferForm}
          />
        </DialogActionsWrapper>
      </Dialog>
    );
  }

  render() {
    const { notifications } = this.props;

    const { showErrors, bulkNameErrors } = this.state;
    const mergedNotifications = [...notifications] || [];

    if (showErrors && bulkNameErrors && bulkNameErrors.length > 0) {
      bulkNameErrors.map((error) => {
        mergedNotifications.push({
          message: error,
          level: "error",
          color: "error",
        });
      });
    }
    const { showPriceOverview } = this.state;

    return (
      <>
        <AmountPriceWrapper
          openPriceOverview={this.openPriceOverview}
          requestHasErrors={this.requestHasErrors}
          {...{ ...this.props, classes: {} }}
        />
        {showPriceOverview && this.renderPriceOverview()}
      </>
    );
  }
}

export default withWidth()(
  withStyles(styles, { name: "AmountPrice" })(AmountPrice)
);
